import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { GripHorizontal } from 'lucide-react';

/**
 * FloatingStack — PDA stack visualization overlay.
 * Draggable, defaults to bottom-right above the canvas toolbar.
 */
export function FloatingStack({ simResult, simStep, states }) {
  const panelRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const [pos, setPos] = useState(null);

  const step = (simResult && simStep >= 0) ? simResult.path?.[simStep] : null;
  const stacks = step?.stacks || [];
  const stepStates = step?.states || [];

  const showDetailed = stacks.length <= 3;

  // Reset position when simulation resets
  useEffect(() => {
    if (!simResult) setPos(null);
  }, [simResult]);

  // --- Drag logic ---
  const onPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const parentRect = panel.offsetParent?.getBoundingClientRect() || { left: 0, top: 0 };
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left - parentRect.left,
      origY: rect.top - parentRect.top,
    };
    const onMove = (me) => {
      if (!dragState.current.dragging) return;
      const dx = me.clientX - dragState.current.startX;
      const dy = me.clientY - dragState.current.startY;
      setPos({ x: dragState.current.origX + dx, y: dragState.current.origY + dy });
    };
    const onUp = () => {
      dragState.current.dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  if (!step || !simResult || simStep < 0 || stacks.length === 0) return null;

  // Default: bottom-right, above the canvas toolbar (~56px from bottom)
  const positionStyle = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : { right: 16, bottom: 80 };

  return (
    <div
      ref={panelRef}
      className="absolute z-30 pointer-events-auto select-none"
      style={positionStyle}
      data-testid="floating-stack"
    >
      <div className="rounded-xl border-2 border-foreground/20 bg-card shadow-xl shadow-black/15 overflow-hidden max-w-[280px] max-h-[260px]">
        {/* Drag handle header */}
        <div
          className="flex items-center px-3 py-1.5 cursor-grab active:cursor-grabbing bg-muted/60 border-b border-border/40"
          onPointerDown={onPointerDown}
          data-testid="stack-drag-handle"
        >
          <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground/60 mr-1.5" />
          <span className="text-[11px] font-semibold text-foreground/70 tracking-wide uppercase">
            Stack
          </span>
        </div>

        {/* Stack content */}
        <div className="p-2.5 overflow-auto" style={{ maxHeight: 210 }}>
          {showDetailed ? (
            <div className="flex items-end gap-3" data-testid="stack-columns">
              {stacks.map((stack, i) => {
                const stateId = stepStates[i] || stepStates[0];
                const stateName = states.find(s => s.id === stateId)?.name || '?';
                return <StackColumn key={i} stack={stack} stateName={stateName} index={i} />;
              })}
            </div>
          ) : (
            <div className="space-y-0.5 text-[11px] font-mono" data-testid="stack-summary">
              <div className="text-[10px] text-muted-foreground mb-1">
                {stacks.length} configs
              </div>
              {stepStates.map((stateId, i) => {
                const stateName = states.find(s => s.id === stateId)?.name || '?';
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    <span className="font-semibold">{stateName}</span>
                    <span className="text-muted-foreground">depth {stacks[i]?.length || 0}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StackColumn({ stack, stateName, index }) {
  const reversed = [...stack].reverse();
  const isEmpty = stack.length === 0;

  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={`stack-column-${index}`}>
      <span className="text-[10px] font-semibold text-muted-foreground">{stateName}</span>
      <div className="flex flex-col border-2 border-border/60 rounded overflow-hidden min-w-[32px]">
        {isEmpty ? (
          <div className="px-1.5 py-1 text-[10px] text-muted-foreground/50 text-center italic">
            empty
          </div>
        ) : (
          reversed.map((sym, i) => (
            <div
              key={i}
              className={`px-2 py-0.5 text-[11px] font-mono text-center border-b border-border/30 last:border-b-0 ${
                i === 0 ? 'bg-primary/15 text-primary font-semibold' : 'bg-muted/30'
              }`}
            >
              {sym}
            </div>
          ))
        )}
      </div>
      <span className="text-[9px] text-muted-foreground/60">depth {stack.length}</span>
    </div>
  );
}
